import { useEffect, useState } from "react";
import { Loader2, ShoppingCart, DollarSign, Download, ChevronRight, ChevronDown, Layers,
  AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { opsApi } from "../../lib/api";
import { toast } from "sonner";
import Hint from "@/components/Hint";

const LIGHT = {
  green: { dot: "bg-emerald-500", chip: "border-emerald-200 bg-emerald-50 text-emerald-800",
    label: "Ready" },
  yellow: { dot: "bg-amber-500", chip: "border-amber-200 bg-amber-50 text-amber-800",
    label: "Caution" },
  red: { dot: "bg-rose-500", chip: "border-rose-200 bg-rose-50 text-rose-800", label: "Blocked" },
};
const STAGE_TONE = { Draft: "bg-slate-200 text-slate-700", Submitted: "bg-indigo-100 text-indigo-700",
  Shipped: "bg-teal-100 text-teal-800", "On hand": "bg-emerald-100 text-emerald-700" };
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2,
  maximumFractionDigits: 2 })}`;
const gid = (g) => `${g.manufacturer || "no-manufacturer"}-${g.cost_code || "no-cost-code"}`;
const sameBucket = (a, b) => a.manufacturer.toLowerCase() === b.manufacturer.toLowerCase()
  && (a.cost_code_id || "") === (b.cost_code_id || "");

/** The traffic light per manufacturer + cost code. Computed from the linked records — the
 *  submittal, the change orders, the costs, the FRS review flag and the contact. */
export default function ReadinessBoard({ data, busy, onPrepare, project, onChanged, onQuoteFor, onOpenPo, expandAll }) {
  const [pricing, setPricing] = useState(null);
  const [open, setOpen] = useState({});
  const board = data.board;
  const onPo = data.on_po || [];
  useEffect(() => { setOpen(Object.fromEntries(board.map((g) => [g.key, !!expandAll]))); }, [expandAll]); // eslint-disable-line react-hooks/exhaustive-deps

  if (board.length === 0 && onPo.length === 0) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-600"
           data-testid="ops-po-board-empty">
        Nothing left to buy. Every FRS row and approved change order line is already on a purchase
        order.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="ops-po-board">
      <p className="text-xs text-slate-400">{board.length} to buy{onPo.length ? ` · ${onPo.length} on a PO` : ""}</p>

      {board.map((g) => {
        const light = LIGHT[g.readiness];
        const isOpen = !!open[g.key];
        const siblings = board.filter((o) => o.key !== g.key && sameBucket(o, g));
        return (
          <div key={g.key} className="rounded-xl border border-slate-200 bg-white"
               data-testid={`ops-po-group-${gid(g)}`}>
            <div className="flex flex-wrap items-center gap-3 px-4 py-3">
              <button type="button" onClick={() => setOpen((o) => ({ ...o, [g.key]: !isOpen }))}
                      className="flex min-w-0 flex-1 flex-wrap items-center gap-3 text-left"
                      data-testid={`ops-po-toggle-${gid(g)}`} aria-expanded={isOpen}>
                {isOpen ? <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
                  : <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />}
                <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${light.dot}`} />
                <span className="text-sm font-semibold text-slate-900">
                  {g.manufacturer || "No manufacturer on the line"}
                </span>
                <span className="font-mono text-xs text-slate-500">
                  {g.cost_code || "no cost code"}{g.cost_code_name ? ` · ${g.cost_code_name}` : ""}
                </span>
                <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${light.chip}`}>
                  {light.label}
                </span>
                {g.late && (
                  <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${g.late === "red" ? "text-rose-600" : "text-amber-600"}`}
                        data-testid={`ops-po-late-${gid(g)}`}
                        title={g.late === "red"
                          ? `Lead time ${g.lead_time_days}d runs past the scope start ${g.start_date} — order now`
                          : `Lead time ${g.lead_time_days}d lands within 10 days of the scope start ${g.start_date}`}>
                    <AlertCircle className="h-3.5 w-3.5" /> {g.late === "red" ? "Late" : "Tight"}
                  </span>
                )}
                {g.quote_expiring?.length > 0 && (
                  <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-amber-600"
                        data-testid={`ops-po-quote-expiring-${gid(g)}`}
                        title={g.quote_expiring.map((q) => `Quote ${q.quote_ref || "(no #)"} valid through ${q.valid_through}`).join("\n")}>
                    <AlertCircle className="h-3.5 w-3.5" /> Quote expiring
                  </span>
                )}
                <span className="text-xs text-slate-500">
                  {g.lines.length} line(s) · lead {g.lead_time_days}d
                </span>
                <span className="ml-auto text-sm font-semibold tabular-nums text-slate-900">{money(g.value)}</span>
              </button>
              {data.can_edit && (
                <div className="flex shrink-0 items-center gap-1.5">
                  <Button size="sm" variant="ghost" className="h-8 text-xs" disabled={busy}
                          onClick={() => onQuoteFor(g)} title="Load a vendor quote for this group"
                          data-testid={`ops-po-quote-${gid(g)}`}>
                    <Download className="mr-1 h-3.5 w-3.5" /> Quote
                  </Button>
                  {siblings.length > 0 && (
                    <Button size="sm" variant="outline" className="h-8 text-xs" disabled={busy}
                            onClick={() => onPrepare({ combine_keys: [g.key, ...siblings.map((s) => s.key)] })}
                            title={`One PO for ${g.manufacturer} · ${g.cost_code || "no cost code"} (${siblings.length + 1} groups)`}
                            data-testid={`ops-po-combine-${gid(g)}`}>
                      <Layers className="mr-1 h-3.5 w-3.5" /> Combine
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="h-8 text-xs" disabled={busy}
                          onClick={() => onPrepare({ keys: [g.key] })}
                          data-testid={`ops-po-prepare-${gid(g)}`}>
                    <ShoppingCart className="mr-1 h-3.5 w-3.5" /> Prepare
                  </Button>
                </div>
              )}
            </div>

            {isOpen && (
              <div className="border-t border-slate-100 px-4 pb-3 pt-2">
                {(g.blockers.length > 0 || g.cautions.length > 0) && (
                  <ul className="mb-2 space-y-0.5 text-xs">
                    {g.blockers.map((b, i) => (
                      <li key={`b${i}`} className="text-rose-700">{b}</li>
                    ))}
                    {g.cautions.map((c, i) => (
                      <li key={`c${i}`} className="text-amber-700">{c}</li>
                    ))}
                  </ul>
                )}
                <LineTable lines={g.lines} onPrice={setPricing} />
              </div>
            )}
          </div>
        );
      })}

      {onPo.map((po) => (
        <button key={po.id} type="button" onClick={() => onOpenPo?.(po.id)}
                data-testid={`ops-po-onpo-${po.po_number}`}
                title={`Open ${po.po_number} in Purchase orders`}
                className="flex w-full flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-left text-slate-500 hover:bg-slate-100">
          <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-slate-300" />
          <span className="text-sm font-semibold">{po.manufacturer}</span>
          <span className="font-mono text-xs">{po.cost_codes.join(", ") || "no cost code"}</span>
          {po.scope_codes.length > 0 && <span className="text-xs">{po.scope_codes.join(" · ")}</span>}
          <span className="font-mono text-xs">{po.po_number}</span>
          <span className="ml-auto text-sm font-semibold tabular-nums">{money(po.total)}</span>
          <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${STAGE_TONE[po.stage] || "bg-slate-200 text-slate-600"}`}
                data-testid={`ops-po-onpo-stage-${po.po_number}`}>{po.stage}</span>
          {po.billing_flag && <span className="rounded-full bg-teal-100 px-2 py-0.5 text-[11px] text-teal-800">bill it</span>}
        </button>
      ))}
      {pricing && <PriceDialog projectId={project?.id} line={pricing} onClose={() => setPricing(null)}
                               onDone={() => { setPricing(null); onChanged?.(); }}
                               onQuote={() => { const l = pricing; setPricing(null);
                                 onQuoteFor(board.find((g) => g.lines.includes(l)) || null); }} />}
    </div>
  );
}


function LineTable({ lines, onPrice }) {
  return (
    <table className="w-full table-fixed text-xs">
      <colgroup>
        <col className="w-36" /><col /><col className="w-20" /><col className="w-28" />
        <col className="w-28" /><col className="w-16" />
      </colgroup>
      <thead className="text-[10px] uppercase tracking-wide text-slate-400">
        <tr>
          <th className="py-1 pr-3 text-left font-medium">Part</th>
          <th className="py-1 pr-3 text-left font-medium">Description</th>
          <th className="py-1 pr-3 text-right font-medium"><Hint text="Total from the FRS, checked against the estimate line and processed change orders">Qty</Hint></th>
          <th className="py-1 pr-3 text-right font-medium"><Hint text="Unit cost from the estimate (vendor quote when one is loaded)">Each</Hint></th>
          <th className="py-1 pr-3 text-right font-medium">Total</th>
          <th className="py-1 text-left font-medium">From</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {lines.map((l, i) => (
          <tr key={l.line_key || i}>
            <td className="truncate py-1.5 pr-3 font-mono" title={l.item_number || ""}>{l.item_number || "—"}</td>
            <td className="truncate py-1.5 pr-3" title={l.description || ""} data-testid={`ops-po-line-desc-${i}`}>
              {l.description || "—"}
            </td>
            <td className="whitespace-nowrap py-1.5 pr-3 text-right tabular-nums" data-testid={`ops-po-line-qty-${i}`}>
              {Number(l.qty).toLocaleString()}
              {l.check && (
                <span className={`ml-1 ${l.check.ok ? "text-emerald-600" : "text-amber-600"}`}
                      title={`FRS ${l.check.frs} · Estimate ${l.check.estimate ?? "—"} · CO ${l.check.co}${l.check.ok ? " — all three agree" : " — docs differ, verify before ordering"}`}>
                  {l.check.ok ? "✓" : "!"}
                </span>
              )}
            </td>
            <td className="whitespace-nowrap py-1.5 pr-3 text-right tabular-nums">
              {l.unit_cost ? (
                <span title={l.quoted ? `Vendor quote${l.quote_ref ? ` ${l.quote_ref}` : ""}` : "Estimate cost"}
                      className={l.quoted ? "text-sky-700" : ""}>{money(l.unit_cost)}</span>
              ) : <NoCost onClick={() => onPrice(l)} testId={`ops-po-nocost-${i}`} />}
            </td>
            <td className="whitespace-nowrap py-1.5 pr-3 text-right font-medium tabular-nums">
              {l.unit_cost ? money(l.total ?? l.unit_cost * l.qty) : <NoCost onClick={() => onPrice(l)} />}
            </td>
            <td className="py-1.5 text-slate-500">{l.source === "co" ? "CO" : "FRS"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}


/** Amber dollar — the line has no cost yet. Click to price it. */
function NoCost({ onClick, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
            className="inline-flex h-5 w-5 items-center justify-center rounded text-amber-500 hover:bg-amber-50">
      <DollarSign className="h-3.5 w-3.5" />
    </button>
  );
}

function PriceDialog({ projectId, line, onClose, onDone, onQuote }) {
  const [cost, setCost] = useState("");
  const [lib, setLib] = useState(true);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!Number(cost)) { toast.error("Enter a unit cost"); return; }
    setBusy(true);
    try {
      await opsApi.purchasingPrice(projectId, {
        unit_cost: Number(cost), item_number: line.item_number || "", description: line.description || "",
        manufacturer: line.manufacturer || "", estimate_line_id: line.estimate_line_id || "",
        frs_item_ids: line.frs_item_ids || [], change_order_line_id: line.change_order_line_id || "",
        add_to_cost_library: lib });
      toast.success("Priced"); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save that price"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-md space-y-3 rounded-xl border border-border bg-card p-5 text-card-foreground shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="ops-po-price-dialog">
        <h3 className="text-base font-semibold">Price this line</h3>
        <p className="text-xs text-slate-500"><span className="font-mono">{line.item_number || "—"}</span> · {line.description} · qty {line.qty}</p>
        <label className="block text-xs">
          <span className="text-slate-500">Unit cost (each)</span>
          <input type="number" step="0.01" min="0" value={cost} onChange={(e) => setCost(e.target.value)} autoFocus
                 className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm dark:border-slate-700 dark:bg-slate-900" data-testid="ops-po-price-input" />
        </label>
        {Number(cost) > 0 && <p className="text-xs text-slate-500">Total {money(Number(cost) * Number(line.qty || 0))}</p>}
        <label className="flex items-center gap-2 text-xs">
          <input type="checkbox" checked={lib} onChange={(e) => setLib(e.target.checked)} data-testid="ops-po-price-lib" />
          Add to the Cost Library{line.item_number ? ` as ${line.item_number}` : ""} so the next estimate has it
        </label>
        <div className="flex flex-wrap justify-end gap-2 pt-1">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="outline" size="sm" onClick={onQuote} data-testid="ops-po-price-quote"><Download className="mr-1.5 h-3.5 w-3.5" /> Load a quote</Button>
          <Button size="sm" disabled={busy} onClick={save} data-testid="ops-po-price-save">{busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />} Save price</Button>
        </div>
      </div>
    </div>
  );
}
